'use client';

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export type RevealMotionState = 'static' | 'pending' | 'revealed' | 'timeline';

/**
 * Motion is opt-in from JavaScript's point of view. If a browser cannot answer
 * the preference query, leave content in its static, readable state rather than
 * hiding it on the assumption that motion is welcome.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;

  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return true;
  }
}

/** CSS feature detection is deliberately guarded: older webviews may not have CSS.supports. */
function supportsViewTimeline(): boolean {
  if (typeof window === 'undefined' || typeof window.CSS?.supports !== 'function') return false;

  try {
    // Keep this probe exactly aligned with the CSS @supports gate below. A
    // browser can parse a view-timeline-name declaration without implementing
    // the animation-timeline: view() behavior that consumes it.
    return window.CSS.supports('animation-timeline: view()');
  } catch {
    return false;
  }
}

function hasIntersectionObserver(): boolean {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}

function safeDelay(delay: number): number {
  return Number.isFinite(delay) ? Math.max(0, delay) : 0;
}

function safeIntersectionThreshold(threshold: number): number {
  if (!Number.isFinite(threshold)) return 0.2;
  return Math.min(1, Math.max(0, threshold));
}

function reachesIntersectionThreshold(
  entry: IntersectionObserverEntry,
  threshold: number
): boolean {
  const ratio = Number.isFinite(entry.intersectionRatio)
    ? entry.intersectionRatio
    : entry.isIntersecting
      ? 1
      : 0;

  // At threshold 0, isIntersecting is the reliable edge signal even when the
  // browser reports a zero-area boundary intersection.
  return threshold === 0 ? entry.isIntersecting || ratio > 0 : ratio >= threshold;
}

interface RevealProps {
  children: ReactNode;
  /** Delay in milliseconds for the shared motion token. */
  delay?: number;
  /** IntersectionObserver threshold used only by the fallback path. */
  threshold?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Transform-only scroll reveal.
 *
 * Server and first client render are static, so hydration never depends on a
 * browser API. After hydration, capable browsers use CSS `view()` timelines;
 * other motion-enabled browsers get one IntersectionObserver fallback. Both
 * paths keep the content in the accessibility tree, and reduced motion (or a
 * missing capability API) leaves it in its final position without observing.
 */
export function Reveal({
  children,
  delay = 0,
  threshold = 0.2,
  className,
  style,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [motionState, setMotionState] = useState<RevealMotionState>('static');
  const motionDelay = safeDelay(delay);
  const observerThreshold = safeIntersectionThreshold(threshold);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let active = true;
    const scheduleState = (next: RevealMotionState) => {
      // Defer capability-derived state until after the subscription is set up;
      // this avoids a cascading render directly from the effect body.
      Promise.resolve().then(() => {
        if (active) setMotionState(next);
      });
    };

    const reduced = prefersReducedMotion();
    const timeline = supportsViewTimeline();
    if (reduced || (!timeline && !hasIntersectionObserver())) {
      active = false;
      return;
    }

    if (timeline) {
      scheduleState('timeline');
      return () => {
        active = false;
      };
    }

    scheduleState('pending');
    let observer: IntersectionObserver;
    try {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          if (!active || !entry) return;
          if (reachesIntersectionThreshold(entry, observerThreshold)) {
            active = false;
            setMotionState('revealed');
            observer.disconnect();
          }
        },
        { threshold: observerThreshold }
      );
    } catch {
      active = false;
      return;
    }

    observer.observe(element);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [observerThreshold]);

  const motionStyle = {
    '--motion-reveal-delay': `${motionDelay}ms`,
    ...style,
  } as CSSProperties;

  return (
    <div
      ref={ref}
      data-motion-state={motionState}
      style={motionStyle}
      className={cn(
        'motion-reveal',
        motionState === 'pending' && 'motion-reveal--pending',
        motionState === 'revealed' && 'motion-reveal--revealed',
        motionState === 'timeline' && 'motion-reveal--timeline',
        className
      )}
    >
      {children}
    </div>
  );
}

export {
  hasIntersectionObserver,
  prefersReducedMotion,
  reachesIntersectionThreshold,
  safeIntersectionThreshold,
  supportsViewTimeline,
};
